import { useMemo, useState } from "react";
import {
  useGetStockAdjustmentsQuery,
  useGetProductsQuery,
  useGetWarehousesQuery,
} from "../../../../redux/api";
import type { StockAdjustment } from "../../../../redux/api";
import { IconPlus, IconSearch, IconEye } from "../../../../components/icons";
import { StockAdjustmentFormModal } from "./stockAdjustment";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 7;

const reasonLabel = (reason: string) =>
  reason
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Inventory → Stock Adjustments (list page). Read-only audit log — there's no
// edit/delete for an adjustment (a correction is a new, opposite adjustment,
// same as an accounting ledger); "New adjustment" is the only action, going
// to the create-only form.
const StockAdjustments = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const {
    data: adjustments = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetStockAdjustmentsQuery();
  const { data: products = [] } = useGetProductsQuery();
  const { data: warehouses = [] } = useGetWarehousesQuery();

  const [search, setSearch] = useState("");

  const productsById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const warehousesById = useMemo(() => new Map(warehouses.map((w) => [w.id, w])), [warehouses]);

  const rows = useMemo(
    () =>
      adjustments.map((a) => ({
        ...a,
        productName: productsById.get(a.productId)?.productName ?? a.productId,
        warehouseName: warehousesById.get(a.warehouseId)?.name ?? a.warehouseId,
      })),
    [adjustments, productsById, warehousesById],
  );

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = rows.find((r) => r.id === viewingId) ?? null;


  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.productName, r.warehouseName, r.reason, r.notes].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [rows, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load stock adjustments"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Stock Adjustments"
          onBack={() => setViewingId(null)}
          title={`${viewing.productName} — ${viewing.warehouseName}`}
          id={viewing.id}
        >
          <OverviewFieldList
            fields={[
              { label: "Product", value: viewing.productName },
              { label: "Warehouse", value: viewing.warehouseName },
              { label: "Date", value: new Date(viewing.createdAt).toLocaleString() },
              {
                label: "Quantity change",
                value: viewing.delta >= 0 ? `+${viewing.delta}` : `${viewing.delta}`,
              },
              { label: "Balance after", value: viewing.quantityAfter },
              { label: "Reason", value: reasonLabel(viewing.reason) },
              {
                label: "Reference",
                value: viewing.referenceType
                  ? `${viewing.referenceType} ${viewing.referenceId ?? ""}`.trim()
                  : null,
              },
              { label: "Notes", value: viewing.notes, full: true },
            ]}
          />
        </EntityOverview>
        <StockAdjustmentFormModal open={modalOpen} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Stock Adjustments</h2>
            <p className="panel__sub">
              Showing {visible.length} of {adjustments.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search by product, warehouse or reason…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setModalOpen(true)}
            >
              <IconPlus /> New adjustment
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Product</th>
                <th>Warehouse</th>
                <th>Delta</th>
                <th>Balance after</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading stock adjustments…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {adjustments.length === 0
                      ? "No stock adjustments yet."
                      : "No adjustments match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((row: StockAdjustment & { productName: string; warehouseName: string }) => (
                  <tr key={row.id}>
                    <td className="cell-date">{new Date(row.createdAt).toLocaleString()}</td>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(row.id)}
                      >
                        {row.productName}
                      </button>
                    </td>
                    <td>{row.warehouseName}</td>
                    <td>
                      <span className={`pill ${row.delta >= 0 ? "pill--neutral" : "pill--danger"}`}>
                        {row.delta >= 0 ? `+${row.delta}` : row.delta}
                      </span>
                    </td>
                    <td>{row.quantityAfter}</td>
                    <td>{reasonLabel(row.reason)}</td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(row.id),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <StockAdjustmentFormModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default StockAdjustments;
